import { IconButton, SvgIcon } from '@mui/material';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AdminEmailLogTable from '@Components/AdminEmailLogTable';
import { FullScreenLoader } from '@Components/index';
import CommonConstants from '@Constants/common.constants';
import Icons from '@Constants/Icons.constants';
import ReportFiltersModal from '@Containers/Report/ReportFiltersModal';
import { EmailLogReportQuery } from '@Models/report.model';
import { useGetEmailLogsQuery } from '@Services/report.service';
import { BaseAlertProps, ReportTypes } from '@Typings/global.types';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import { getEmailLogReportRTKQuery } from '@Utils/report/report.utils';

import AdminEmailLogProps from './AdminEmailLog.types';
import { GetRows } from './AdminEmailLog.utils';

const AdminEmailLog: FunctionComponent<AdminEmailLogProps> = (
  props: AdminEmailLogProps
): JSX.Element => {
  /** HOOKS */
  const location = useLocation();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  /** QUERIES */
  const emailLogReportQuery: EmailLogReportQuery = useMemo(
    () => getEmailLogReportRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const { data, isLoading, isFetching, error } = useGetEmailLogsQuery(
    emailLogReportQuery,
    { skip: !query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) }
  );

  const [open, setOpen] = useState<{ [key: string]: boolean }>({});

  const handleClick = (id: string) => {
    if (open[id]) {
      Object.keys(open).forEach((key) => {
        if (key.includes(id)) {
          setOpen((prevState) => ({
            ...prevState,
            [key]: false,
          }));
        }
      });
    } else {
      setOpen((prevState) => ({
        ...prevState,
        [id]: true,
      }));
    }
  };

  const getId = (emailId: string): string => `${emailId}`;

  /** Table Actions Component */
  const actionsMenuComponent = (id: string): JSX.Element => (
    <IconButton className="clearBtn" onClick={() => handleClick(getId(id))}>
      <SvgIcon>{open[id] ? Icons.arrowDropUp : Icons.arrowDropDown}</SvgIcon>
    </IconButton>
  );

  /** ROWS AND TOTAL_ROWS_COUNT */
  const { rows } = GetRows(data?.table, actionsMenuComponent, open);
  const totalRowsCount = data?.table[0]?.fullCount
    ? parseInt(data?.table[0].fullCount, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Show error for get query */
  useEffect(() => {
    if (error) {
      triggerAlert(error, setAlertState);
    }
  }, [error, triggerAlert]);

  /** OPEN FILTERS MODAL */
  const openFilters = () => {
    setModalOpen(true);
  };

  return isLoading ? (
    <FullScreenLoader />
  ) : (
    <>
      <ReportFiltersModal
        isAdmin
        initialReportType={ReportTypes.EmailLog}
        open={modalOpen}
        setOpen={setModalOpen}
      />
      <AdminEmailLogTable
        alertState={alertState}
        showFiltersBtnProps={{
          onClick: openFilters,
        }}
        tableProps={{
          rows,
          paginationProps: {
            count: totalRowsCount,
          },
          loading: isLoading || isFetching,
        }}
        {...props}
      />
    </>
  );
};

export default AdminEmailLog;
